import React, { useEffect, useRef, useState } from 'react';

import {
  Breakpoints,
  CTA,
  CustomImage,
  SupDisclaimer,
  TextAlignment,
  TextCustom,
  TextLineHeight,
  TextSize,
  TextTag,
  TextWeight,
  useModalDisclaimer,
} from '@vw-marketing/us-components';

import { AppConstants } from '../../utils/app-constants';
import { CarImage } from '../car-image/CarImage';
import { DisclaimerKeys } from '../../utils/disclaimer';
import { ModelConfig } from '../../types/model';
import {
  onVWBasicVehicleButtonClick,
  onVWBasicVehicleSelectClick,
} from '../../tagging/tagging-helpers';
import { mapVehicleToFeatureAppContent, separateThousand, transformIrisUrl } from '../../utils/general';
import { navigateFullPage } from '../../utils/navigate-full-page';
import { useImagePreloader } from '../../hooks/use-image-preloader';
import {
  StyledAwardLogoContainer,
  StyledButtonsContainer,
  StyledCardWrapper,
  StyledPriceWrapper,
  StyledVehicleContentWrapper,
  StyledVehicleInformation,
} from './styles';
import { CR_LOGO_SCENE7_EXTENSION, IIHS_LOGO_SCENE7_EXTENSION } from '../../fetch-data/fetch-bg-image';
import { useFeatureAppConfig } from '../../hooks/use-feature-app-config';
import { useDisclaimerKeysByModelAndPage } from '../../hooks/use-disclaimer-configuration';
import { useFeatureAppContent } from '../../hooks/use-feature-app-content';
import { useFeatureServices } from '../../hooks/use-feature-services';
import { useModelsCarouselContext } from '../../hooks/use-models-carousel-context';
import { useSceneSeven } from '../../hooks/use-scene-seven';
import { useSharedConfigs } from '../../context/shared-configs-context';
import { useTrackingManager } from '../../hooks/use-tracking-manager';
import { Vehicle } from '../../types/vehicle';

interface CarouselProps {
  vehicle: ModelConfig;
}

const CUSTOM_INVENTORY_URLS = new Map<string, string>([
  [
    'golf-r',
    '/en/inventory/results.html/__app/search/cars.app?t_cartype=N&t_model=BQCJ',
  ],
]);

// Slides a logo in from the right and out to the left, so the motion always reads as
// right-to-left regardless of which logo is entering/exiting. Once a logo finishes exiting
// it is silently (no transition) parked back off-screen to the right, ready for its next entrance.
const useLogoSlidePosition = (isActive: boolean) => {
  const [position, setPosition] = useState<0 | -100 | 100>(isActive ? 0 : 100);
  const [instant, setInstant] = useState(false);
  const isMountRef = useRef(true);

  useEffect(() => {
    if (isMountRef.current) {
      isMountRef.current = false;
      return;
    }

    setInstant(false);

    if (isActive) {
      setPosition(0);
      return;
    }

    setPosition(-100);

    // Must match the 500ms CSS transition in styles.ts.
    const timeout = setTimeout(() => {
      setInstant(true);
      setPosition(100);
    }, 500);

    return () => clearTimeout(timeout);
  }, [isActive]);

  return { position, instant };
};

export const CarouselCard: React.FC<CarouselProps> = ({ vehicle }) => {
  const { translationHandler } = useModelsCarouselContext();
  const featureAppContent = useFeatureAppContent();
  const faContentVehicle = mapVehicleToFeatureAppContent(featureAppContent, vehicle);
  const trackingManager = useTrackingManager();

  const isCustomImage: boolean = vehicle?.overrideDefaultImage?.enabled;
  const backGroundColor: string = isCustomImage
    ? vehicle.overrideDefaultImage.backgroundColor
    : vehicle.background;

  const { faHostServerUrl = '' } = useFeatureAppConfig();

  const { 'navigation-service': navigateService } = useFeatureServices();

  const errorImage: string = faHostServerUrl + AppConstants.Ngw6Logo480x216;

  const [validImageUrl, setValidImageUrl] = useState<string>(
    isCustomImage ? vehicle.overrideDefaultImage.frontView : vehicle.carImage,
  );

  const [validSideViewImageUrl, setValidSideViewImageUrl] = useState<string>(
    isCustomImage ? vehicle.overrideDefaultImage.imageUrl : vehicle.carImage,
  );

  const { offerTagsConfig: offerTags } = useSharedConfigs();
  const [isHovered, setIsHovered] = useState(false);

  const hasOffer: boolean =
    offerTags.enable && Boolean(offerTags?.tags?.[vehicle.slug]?.length);

  useEffect(() => {
    const img = new Image();
    const sideViewImg = new Image();

    if (!validImageUrl) return setValidImageUrl(errorImage);
    if (!validSideViewImageUrl) return setValidSideViewImageUrl(errorImage);

    const imgSrc = isCustomImage
      ? vehicle.overrideDefaultImage.frontView
      : vehicle.carImage;

    const sideViewImgSrc = isCustomImage
      ? vehicle.overrideDefaultImage.imageUrl
      : vehicle.carImage;

    img.src = imgSrc;
    sideViewImg.src = sideViewImgSrc;

    img.onload = () => setValidImageUrl(imgSrc);
    img.onerror = () => setValidImageUrl(errorImage);

    sideViewImg.onload = () => setValidSideViewImageUrl(sideViewImgSrc);
    sideViewImg.onerror = () => setValidSideViewImageUrl(errorImage);
  }, [
    vehicle.carImage,
    vehicle.overrideDefaultImage?.frontView,
    vehicle.overrideDefaultImage?.imageUrl,
    isCustomImage,
    errorImage,
  ]);

  const faContentFrontView = faContentVehicle?.jellyBeanImageFrontView;
  const faContentSideView = faContentVehicle?.jellyBeanImageSideView;
  const faContentIIHSLogo = faContentVehicle?.IIHSImagePath;
  const faContentCRLogo = faContentVehicle?.consumerReportsImagePath;

  const showIIHSLogo: boolean | undefined = faContentVehicle?.showIIHSLogo?.includes("true") || false;
  const showCRLogo: boolean = faContentVehicle?.showConsumerReportsLogo?.includes("true") || false;

  const scene7FrontSrc = useSceneSeven(faContentFrontView ?? '');
  const scene7SideViewSrc = useSceneSeven(faContentSideView ?? '');
  const scene7IIHSLogo = useSceneSeven(faContentIIHSLogo ?? '', IIHS_LOGO_SCENE7_EXTENSION);
  const scene7CRLogo = useSceneSeven(faContentCRLogo ?? '', CR_LOGO_SCENE7_EXTENSION);

  const imageSrc: string = faContentFrontView
    ? scene7FrontSrc
    : transformIrisUrl(
      validImageUrl,
      AppConstants.IrisImgPositionE13,
      '480',
      '216',
      vehicle.modelYear,
      vehicle.name,
      vehicle.isNewImage || false,
    );

  const sideViewImageSrc: string = faContentSideView
    ? scene7SideViewSrc
    : transformIrisUrl(
      validSideViewImageUrl,
      AppConstants.IrisImgPositionE13,
      '480',
      '216',
      vehicle.modelYear,
      vehicle.name,
      vehicle.isNewImage || false,
    );

  const IIHSLogoSrc: string = (faContentIIHSLogo && scene7IIHSLogo) || (faHostServerUrl + AppConstants.IIHSLogo);
  const CRLogoSrc: string = (faContentCRLogo && scene7CRLogo) || (faHostServerUrl + AppConstants.ConsumerReportsLogo);

  const { frontBlobUrl, sideBlobUrl } = useImagePreloader({ imageSrc, sideViewImageSrc });

  const currentImageSrc = isHovered && (sideBlobUrl || sideViewImageSrc)
    ? sideBlobUrl || sideViewImageSrc
    : frontBlobUrl || imageSrc;

  // All disclaimer numbers for this vehicle on the landing page, in API order.
  // Index 0 = first matching disclaimer (IIHS if present), last = price.
  const disclaimerKeys = useDisclaimerKeysByModelAndPage(
    DisclaimerKeys.LandingKey,
    vehicle?.slug,
  );
  const iihsDisclaimerKey = disclaimerKeys.length > 1 ? disclaimerKeys[0] : undefined;
  const priceDisclaimerKey = disclaimerKeys[disclaimerKeys.length - 1];
  const isIdBuzz: boolean = vehicle.slug.includes('id.-buzz');

  type ActiveLogo = 'iihs' | 'cr';
  const [activeLogo, setActiveLogo] = useState<ActiveLogo>('iihs');

  const iihsSlide = useLogoSlidePosition(!showCRLogo || activeLogo === 'iihs');
  const crSlide = useLogoSlidePosition(!showIIHSLogo || activeLogo === 'cr');

  const { active: isDisclaimerModalActive, activeNumber: activeDisclaimerNumber } = useModalDisclaimer();
  const isAnimationPaused = Boolean(
    isDisclaimerModalActive && iihsDisclaimerKey && activeDisclaimerNumber === iihsDisclaimerKey,
  );

  useEffect(() => {
    if (!showIIHSLogo || !showCRLogo || isAnimationPaused) return;

    const timeout = setTimeout(() => {
      setActiveLogo(prev => prev === 'iihs' ? 'cr' : 'iihs');
    }, 1850);

    return () => clearTimeout(timeout);
  }, [activeLogo, isAnimationPaused, showIIHSLogo, showCRLogo]);

  const handleCTAModelsPage = (source: 'image' | 'cta') => {
    const formattedSlug: string = vehicle.slug
      .replace(/\./g, '-')
      .replace('--', '-'); //prevent id.-buzz become id--buzz
    const currentDomain =
      typeof window !== 'undefined' ? window.location.hostname : 'vw.com';
    const modelsPath: string = `/en/models/${formattedSlug}.html`;
    const modelsUrl: string = `${currentDomain}${modelsPath}`;

    onVWBasicVehicleSelectClick(
      trackingManager,
      modelsUrl,
      'Models',
      vehicle as unknown as Vehicle,
      source === 'image'
        ? 'MiniMOFA Vehicle Image click'
        : `MiniMOFA ${translationHandler('carousel.card.model.name.cta')} ${vehicle.name} CTA click`,
    );

    const navigationObject = navigateService.navigateTo(formattedSlug, {});

    if (!navigationObject) {
      // The host has no page registered under this vehicle's slug (e.g. a
      // missing/mistyped page-repository entry). Fall back to a full page
      // load so the click still lands somewhere instead of silently no-oping.
      console.error(
        `navigateService.navigateTo could not resolve a page named "${formattedSlug}". Falling back to a full navigation to "${modelsPath}" for "${vehicle.name}".`,
      );
      navigateFullPage(modelsPath);
      return;
    }

    navigationObject.push();
  };

  const handleCTARedirect = () => {
    try {
      const customInventoryUrl = CUSTOM_INVENTORY_URLS.get(vehicle.slug);

      const currentDomain = typeof window !== 'undefined' ? window.location.hostname : 'vw.com';

      const inventoryUrl: string =
        customInventoryUrl ||
        (vehicle.inventoryUrl ??
          `/en/inventory.html/__app/inventory/results/${vehicle.slug}`);

      const offersUrl: string = `/en/offers.html?model-app=${vehicle.name}`;

      onVWBasicVehicleButtonClick(
        trackingManager,
        `${currentDomain}${hasOffer ? offersUrl : inventoryUrl}`,
        hasOffer
          ? translationHandler('carousel.card.offers.cta')
          : translationHandler('carousel.card.inventory.cta'),
        vehicle,
        hasOffer
          ? 'MiniMOFA View Offers CTA click'
          : 'MiniMOFA Search Inventory CTA click',
      );

      const pageName = hasOffer ? 'offers' : 'gsl_results';
      const appName = hasOffer ? 'US-SofaTier1V1' : 'gsl_pages';

      const search = inventoryUrl.includes('?')
        ? inventoryUrl.substring(inventoryUrl.indexOf('?'))
        : '';

      const navigationData = {
        [appName]: hasOffer
          ? {
            pathname: '/',
            search: `?model-app=${vehicle.name}`,
          }
          : {
            pathname: '/',
            search,
          },
      };

      const navigationObject = navigateService.navigateTo(
        pageName,
        navigationData,
      );

      if (!navigationObject) {
        // Same fallback rationale as handleCTAModelsPage: the host has no
        // page registered under "gsl_results"/"offers", so fall back to a
        // full page load using the same URL already computed for tracking.
        const fallbackPath = hasOffer ? offersUrl : inventoryUrl;

        console.error(
          `navigateService.navigateTo could not resolve a page named "${pageName}". Falling back to a full navigation to "${fallbackPath}" for "${vehicle.name}".`,
        );
        navigateFullPage(fallbackPath);
        return;
      }

      navigationObject.push();
    } catch (error) {
      console.error('Error triggering handleCTARedirect');
      console.error(error);
    }
  };

  const handleMouseEnter = () => {
    if (sideViewImageSrc) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <StyledCardWrapper
      className="card-wrapper"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-testid="card-wrapper"
    >
      <div style={{ width: "100%" }}>
        <CarImage
          backgroundColor={backGroundColor}
          backgroundImage={vehicle.backgroundImage}
          errorImage={errorImage}
          imageAlt={vehicle.carImageAlt}
          imageSrc={currentImageSrc}
          isCustomImage={isCustomImage}
          isNewImage={vehicle.isNewImage}
          isIdBuzz={isIdBuzz}
          onClick={() => {
            handleCTAModelsPage('image');
          }}
        />
      </div>
      <StyledVehicleContentWrapper className="content-wrapper">
        <StyledAwardLogoContainer>
          {showIIHSLogo && (
            <div
              className="award-logo"
              style={{
                transform: `translateX(${iihsSlide.position}%)`,
                transitionDuration: iihsSlide.instant ? '0ms' : undefined,
              }}
            >
              <CustomImage
                alt='IIHS Logo'
                src={IIHSLogoSrc}
                srcErrorImage={errorImage}
                srcPlaceholder={AppConstants.PlaceHolder480x216}
              />
              <TextCustom
                tag={TextTag.span}
                style={{
                  [Breakpoints.default]: {
                    fontWeight: TextWeight.bold,
                    fontSize: TextSize.TextSize16,
                    lineHeight: TextLineHeight.TextLineHeight24,
                  },
                }}
              >
                {iihsDisclaimerKey && (
                  <span className="sup-disclaimer">
                    {/*@ts-ignore */}
                    <SupDisclaimer>{iihsDisclaimerKey}</SupDisclaimer>
                  </span>
                )}
              </TextCustom>
            </div>
          )}
          {showCRLogo && (
            <div
              className="award-logo"
              style={{
                transform: `translateX(${crSlide.position}%)`,
                transitionDuration: crSlide.instant ? '0ms' : undefined,
              }}
            >
              <CustomImage
                alt='Consumer Reports Logo'
                src={CRLogoSrc}
                srcErrorImage={errorImage}
                srcPlaceholder={AppConstants.PlaceHolder480x216}
              />
            </div>
          )}
        </StyledAwardLogoContainer>
        <StyledVehicleInformation className="vehicle-information">
          <TextCustom
            style={{
              [Breakpoints.default]: {
                fontWeight: TextWeight.bold,
                fontSize: TextSize.TextSize24,
                lineHeight: TextLineHeight.TextLineHeight36,
                textAlign: TextAlignment.center,
              },
            }}
          >
            {vehicle.name}
          </TextCustom>
          <TextCustom
            style={{
              [Breakpoints.default]: {
                fontSize: TextSize.TextSize16,
                lineHeight: TextLineHeight.TextLineHeight24,
                textAlign: TextAlignment.left,
              },
            }}
          >
            {vehicle.description}
          </TextCustom>

          {vehicle.price && (
            <StyledPriceWrapper>
              <TextCustom
                tag={TextTag.span}
                style={{
                  [Breakpoints.default]: {
                    fontWeight: TextWeight.bold,
                    fontSize: TextSize.TextSize16,
                    lineHeight: TextLineHeight.TextLineHeight24,
                  },
                }}
              >
                {translationHandler('carousel.card.price')}
                {separateThousand(vehicle.price)}
              </TextCustom>
              <TextCustom
                tag={TextTag.span}
                style={{
                  [Breakpoints.default]: {
                    fontWeight: TextWeight.bold,
                    fontSize: TextSize.TextSize16,
                    lineHeight: TextLineHeight.TextLineHeight24,
                  },
                }}
              >
                {priceDisclaimerKey && (
                  <span className="sup-disclaimer">
                    {/*@ts-ignore */}
                    <SupDisclaimer>{priceDisclaimerKey}</SupDisclaimer>
                  </span>
                )}
              </TextCustom>
            </StyledPriceWrapper>
          )}
        </StyledVehicleInformation>
        <StyledButtonsContainer className="buttons-container">
          <CTA
            tag="button"
            emphasis="secondary"
            onClick={() => {
              handleCTAModelsPage('cta');
            }}
            stretchContent
          >
            <TextCustom
              style={{
                [Breakpoints.default]: {
                  fontSize: TextSize.TextSize16,
                  lineHeight: TextLineHeight.TextLineHeight20,
                  textAlign: TextAlignment.center,
                },
              }}
            >
              {translationHandler('carousel.card.model.name.cta')}{' '}
              {vehicle.name}
            </TextCustom>
          </CTA>
          <CTA
            tag="button"
            emphasis={hasOffer ? 'offer' : 'primary'}
            onClick={handleCTARedirect}
            stretchContent
          >
            <TextCustom
              style={{
                [Breakpoints.default]: {
                  fontSize: TextSize.TextSize16,
                  lineHeight: TextLineHeight.TextLineHeight20,
                  textAlign: TextAlignment.center,
                },
              }}
            >
              {translationHandler(
                hasOffer
                  ? 'carousel.card.offers.cta'
                  : 'carousel.card.inventory.cta',
              )}
            </TextCustom>
          </CTA>
        </StyledButtonsContainer>
      </StyledVehicleContentWrapper>
    </StyledCardWrapper>
  );
};
